import { DateTime } from "luxon";
import React from "react";
import { Updater } from "use-immer";

import { GenericExperienceFe } from "@intelligems/ig-types";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import {
  ScheduleActionType,
  ScheduleErrors,
} from "@src/features/scheduleExperience/types";
import { theme } from "@src/theme";

interface SimpleDateTimePickerProps {
  dateTime: DateTime | null;
  label?: string;
  onTimeChange: (newVal: DateTime | null) => void;
  resourceMinTime: DateTime | undefined;
  width?: string;
  minutesStep?: number;
  disabled?: boolean;
  setErrors: Updater<ScheduleErrors>;
  timezone?: string;
  timeNow: DateTime;
  action: ScheduleActionType;
  experience: GenericExperienceFe;
  disablePast: boolean;
  setDisableTrash: Updater<boolean>;
}

export const SimpleDateTimePicker = ({
  dateTime,
  resourceMinTime,
  onTimeChange,
  label,
  disabled,
  width,
  minutesStep,
  setErrors,
  timezone,
  timeNow,
  action,
  experience,
  disablePast,
  setDisableTrash,
}: SimpleDateTimePickerProps) => {
  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DateTimePicker
        timezone={timezone ?? "UTC"}
        slotProps={{
          digitalClockSectionItem: {
            sx: {
              "&.Mui-selected": {
                backgroundColor: `${theme.igBackgroundColors.interactive.dark.active} !important`,
              },
            },
          },
          textField: { helperText: `Timezone: ${timezone ?? "UTC"}` },
        }}
        disablePast={disablePast}
        closeOnSelect={false}
        disabled={disabled}
        sx={{ width: width || undefined }}
        minDateTime={resourceMinTime}
        maxDateTime={timeNow.plus({ years: 1 })}
        label={label || "Select date and time"}
        value={dateTime}
        minutesStep={minutesStep}
        onChange={(newValue) => {
          onTimeChange(newValue);
          setErrors((draft) => {
            draft[action] = false;
            return draft;
          });
        }}
        onOpen={() => {
          setDisableTrash(true);
        }}
        onClose={() => {
          setDisableTrash(false);
        }}
        onError={(newError) => {
          // when an error no longer exists, a null error is passed
          if (newError === null) {
            setErrors((draft) => {
              draft[action] = false;
              return draft;
            });
          } else if (
            action !== "start" ||
            (action === "start" && experience.status !== "started")
          ) {
            setErrors((draft) => {
              draft[action] = true;
              return draft;
            });
          }
        }}
      />
    </LocalizationProvider>
  );
};
